import { useEffect, useRef } from 'react';
import type { IdentityAskState } from '../sdk/identity-ask';
import { getString, type UiStringKey } from '../sdk/ui-strings';

/** A proposal can fill an empty draft, but must never replace the user's edits. */
export function useIdentityDraft(
  ask: IdentityAskState | null | undefined,
  draft: string,
  setDraft: (value: string) => void,
  focus?: () => void,
) {
  const previous = useRef('');
  const consumed = useRef('');
  const injected = useRef('');
  const draftRef = useRef(draft);
  draftRef.current = draft;
  useEffect(() => {
    if (ask?.status === 'proposed') {
      if (consumed.current === ask.askId || injected.current === ask.askId) return;
      const value = ask.displayValue || ask.value || '';
      if (!draftRef.current || draftRef.current === previous.current) {
        injected.current = ask.askId;
        if (draftRef.current !== value) { setDraft(value); focus?.(); }
      }
      previous.current = ask.displayValue || ask.value || '';
    } else if (!ask || ask.status === 'resolved' || ask.status === 'asked') {
      if (previous.current && draftRef.current === previous.current) setDraft('');
      previous.current = '';
    }
  }, [ask?.askId, ask?.status, draft]);
  return () => { consumed.current = ask?.askId || ''; };
}

export function IdentityComposerHint({ ask, onReject, disabled }: {
  ask?: IdentityAskState | null; onReject?: () => void; disabled?: boolean;
}) {
  if (!ask || ask.status === 'resolved') return null;
  return <div aria-live="polite" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, lineHeight: 1.5, padding: '7px 12px', color: 'inherit' }}>
    <span style={{ flex: 1 }}>
      {getString(`identity.helper.${ask.field}.${ask.status}` as UiStringKey)}
      {ask.mandatory && <> · {getString('identity.required')}</>}
    </span>
    {ask.status === 'proposed' && <button type="button" disabled={disabled} onClick={onReject}
      aria-label={getString('identity.reject')} style={{ color: 'inherit', border: 0, background: 'transparent', cursor: 'pointer', padding: 8, fontSize: 18 }}>×</button>}
  </div>;
}
